import { FC, useState } from 'react';

import { Form, Table } from 'antd';
import moment from 'moment';
import useSWR from 'swr';

import { SimulationResult } from '@/types/simulations';

interface SelectSimulationProps {
  selectedSimulations: SimulationResult[];
  onSelectedSimulationsChange: (simulations: SimulationResult[]) => void;
}

const SelectSimulation: FC<SelectSimulationProps> = ({
  selectedSimulations,
  onSelectedSimulationsChange,
}) => {
  const [pageSize, setPageSize] = useState<number>(10);
  const [offset, setOffset] = useState<number>(1);

  const {
    data: completedSimulationsData,
    isLoading: completedSimulationsDataIsLoading,
  } = useSWR(
    `/simulation/?limit=${pageSize}&offset=${
      (offset - 1) * pageSize
    }&status=SUCCESS`,
    {
      revalidateOnFocus: false,
    }
  );

  const columns = [
    {
      width: '25%',
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      width: 100,
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      width: 100,
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      width: 100,
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
    },
    {
      width: 100,
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) =>
        moment(createdAt).format('DD.MM.YYYY HH:mm'),
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    const currentPageSimulations = completedSimulationsData?.results || [];

    const previousSelectedSims = selectedSimulations.filter(
      (sim) =>
        !currentPageSimulations.some(
          (currentSim: SimulationResult) => currentSim.id === sim.id
        )
    );

    const newSelectedSims = currentPageSimulations.filter(
      (sim: SimulationResult) => newSelectedRowKeys.includes(sim.id)
    );
    const allSelectedSims = [...previousSelectedSims, ...newSelectedSims];

    onSelectedSimulationsChange(allSelectedSims);
  };

  const rowSelection = {
    selectedRowKeys: selectedSimulations.map((sim) => sim.id),
    onChange: onSelectChange,
  };

  return (
    <Form layout="vertical">
      <Table
        rowKey="id"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={completedSimulationsData?.results}
        loading={completedSimulationsDataIsLoading}
        size="small"
        pagination={{
          total: completedSimulationsData?.count,
          pageSize: pageSize,
          hideOnSinglePage: true,
          defaultCurrent: 1,
          onChange: (page: number) => {
            setOffset(page);
          },
          pageSizeOptions: ['5', '10', '20', '50', '100'],
          onShowSizeChange: (current, size) => {
            setOffset(current);
            setPageSize(size);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: 1080 }}
      />
    </Form>
  );
};

export default SelectSimulation;
